import moment from 'moment';
import { mapValues } from 'lodash-es';
import { mapGetters } from '@/store/utils/record-mapper';
import workingWeek from '@/utils/helpers/workingWeek';

// Most permission checks consist of an activePage check and a permission check
const permissions = mapValues(
  {
    canAddTasks: ['tasks', 'canAddTasks'],
    canAddMessages: ['messages', 'canAddMessages'],
    canLogTime: ['time', 'canLogTime', true],
    canViewForms: ['forms', 'canViewForms'],
    canAddForms: ['forms', 'canAddForms'],
    canManageProjectBudget: ['budgets', 'canManageProjectBudget'],
  },
  ([activePage, permission, cantBeTemplate]) =>
    (record) =>
      record.activePages[activePage] &&
      record.permissions[permission] &&
      (!cantBeTemplate || record.type !== 'projects-template'),
);

const isAdmin = (record, getters, { user }, rootGetters, id) =>
  (record && record.permissions.projectAdministrator) ||
  (getters.isTemplate(id) && rootGetters['permissions/canManageProjectTemplates']) ||
  user.administrator;

// These will be record-mapped, but also applied within the 'current' namespace
const recordGetters = {
  ...permissions,
  isArchived: ({ status }) => status === 'archived',
  isActive: ({ status }) => status === 'active',
  isDeleted: ({ status }) => status === 'deleted',
  isCompleted: ({ subStatus }) => subStatus === 'completed',
  isIncomplete: (record, getters, rootState, rootGetters, id) => !getters.isCompleted(id),
  isTemplate: (record) => record && record.type === 'projects-template',
  isNonTemplate: (record, getters, rootState, rootGetters, id) => !getters.isTemplate(id),
  isAdmin,
  canManageProjectCustomFields: (record) => record.permissions.canManageCustomFields,
  canManageProjectBudget: (record) => record.permissions.canManageProjectBudget,
  tasklists: (state, getters, rootState, rootGetters, id) => getters['tasklists/ordered'](id),

  blackoutDays: (state, getters, rootState) => {
    if (!rootState.account.skipWeekends) {
      return state.skipWeekends ? [0, 6] : [];
    }
    return [0, 6];
  },
  workingWeek: (state, getters, rootState, rootGetters, id) => workingWeek(getters.blackoutDays(id)),

  canAddUpdate: (record, getters, rootState, rootGetters, id) =>
    rootGetters['account/features'].includes('projectupdates') &&
    (rootGetters['project/isAdmin'](id) || record.permissions.canAddProjectUpdate) &&
    record.type !== 'projects-template',

  subStatusFromDates: ({ startDate, endDate }) => {
    if (startDate && startDate.diff(moment(), 'days') > 0) {
      return 'upcoming';
    }
    if (endDate && endDate.diff(moment(), 'days') < 0) {
      return 'late';
    }
    return 'current';
  },
};

export default {
  current: (state) => state.currentProjectId && state.records[state.currentProjectId],

  // Create 'current/' prefixed getters for all recordGetters
  ...Object.keys(recordGetters).reduce(
    (currentGetters, key) => ({
      ...currentGetters,
      [`current/${key}`]: (state, getters, ...others) =>
        getters.current && recordGetters[key](getters.current, getters, ...others, getters.current.id),
    }),
    {},
  ),

  ...mapGetters(recordGetters),
};
